import type { MediaRef, TranslatableString } from '@geoic/shared';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Input } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { RichTextInput } from '@/components/form/RichTextInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { Repeater } from '@/features/blocks/BlockFields.js';
import { ContentListPage } from '../ContentListPage.js';
import { ContentEditPage } from '../ContentEditPage.js';
import { createContentApi, type ContentRecord } from '../content-api.js';

type BioRow = { year: string; text: TranslatableString };

export type StaffRecord = ContentRecord & {
  fullName: TranslatableString;
  position: TranslatableString;
  photo: MediaRef | null;
  biography: TranslatableString;
  biographyTimeline: BioRow[];
  phone: string | null;
  email: string | null;
  receptionHours: TranslatableString;
};

export const staffApi = createContentApi<StaffRecord>('/staff');

/**
 * Rahbariyat (TZ 7.1).
 *
 * Tartib SURISH orqali — lavozim ierarxiyasi alifbo tartibida emas:
 * direktor birinchi turishi kerak.
 */
export function StaffListPage() {
  return (
    <ContentListPage<StaffRecord>
      title="Rahbariyat"
      collection="staff"
      contentApi={staffApi}
      basePath="/staff"
      createLabel="Rahbar qo‘shish"
      sortable
      emptyTitle="Hozircha rahbar qo‘shilmagan"
      emptyHint="Tartibni surib o‘zgartirasiz — direktor birinchi turishi kerak."
      columns={[
        {
          key: 'fullName',
          header: 'F.I.Sh.',
          render: (row) => (
            <span className="font-medium text-n-800">{row.fullName?.uz || '(nomsiz)'}</span>
          ),
        },
        {
          key: 'position',
          header: 'Lavozim',
          hideOnTablet: true,
          render: (row) => <span className="text-n-600">{row.position?.uz || '—'}</span>,
        },
      ]}
    />
  );
}

type StaffForm = {
  fullName: TranslatableString;
  position: TranslatableString;
  slug: string;
  photo: MediaRef | null;
  biography: TranslatableString;
  biographyTimeline: BioRow[];
  phone: string;
  email: string;
  receptionHours: TranslatableString;
};

export function StaffEditPage() {
  return (
    <ContentEditPage<StaffForm>
      collection="staff"
      contentApi={staffApi}
      basePath="/staff"
      backLabel="Rahbariyat"
      newTitle="Yangi rahbar"
      titleOf={(form) => form.fullName.uz}
      empty={() => ({
        fullName: { uz: '', ru: '', en: '' },
        position: { uz: '', ru: '', en: '' },
        slug: '',
        photo: null,
        biography: { uz: '', ru: '', en: '' },
        biographyTimeline: [],
        phone: '',
        email: '',
        receptionHours: { uz: '', ru: '', en: '' },
      })}
      fromRecord={(record) => {
        const staff = record as StaffRecord;
        return {
          fullName: staff.fullName ?? { uz: '', ru: '', en: '' },
          position: staff.position ?? { uz: '', ru: '', en: '' },
          slug: staff.slug ?? '',
          photo: staff.photo ?? null,
          biography: staff.biography ?? { uz: '', ru: '', en: '' },
          biographyTimeline: staff.biographyTimeline ?? [],
          phone: staff.phone ?? '',
          email: staff.email ?? '',
          receptionHours: staff.receptionHours ?? { uz: '', ru: '', en: '' },
        };
      }}
      toInput={(form) => ({
        fullName: form.fullName,
        position: form.position,
        slug: form.slug,
        photo: form.photo,
        biography: form.biography,
        biographyTimeline: form.biographyTimeline,
        phone: form.phone || null,
        email: form.email || null,
        receptionHours: form.receptionHours,
      })}
      renderFields={({ form, setForm, fieldErrors }) => (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Card>
              <CardHeader title="Ma’lumot" />
              <CardBody className="space-y-4">
                <TranslatableInput
                  label="F.I.Sh."
                  required
                  value={form.fullName}
                  onChange={(fullName) => setForm((current) => ({ ...current, fullName }))}
                  error={fieldErrors['fullName.uz']}
                />
                <TranslatableInput
                  label="Lavozim"
                  required
                  value={form.position}
                  onChange={(position) => setForm((current) => ({ ...current, position }))}
                  error={fieldErrors['position.uz']}
                />
                <RichTextInput
                  label="Ma’lumotnoma"
                  value={form.biography}
                  onChange={(biography) => setForm((current) => ({ ...current, biography }))}
                  hint="Ta’lim, mehnat faoliyati, yutuqlar"
                />
              </CardBody>
            </Card>

            {/*
              Xronologiya to‘ldirilsa, saytda ma’lumotnoma o‘rniga
              «yil — matn» qatorlari chiqadi (maket `C7`). Bo‘sh
              qoldirilsa yuqoridagi matn ko‘rsatiladi.
            */}
            <Card>
              <CardHeader title="Xronologiya" />
              <CardBody>
                <Repeater<BioRow>
                  label="Qatorlar"
                  max={40}
                  addLabel="Qator qo‘shish"
                  emptyHint="Bo‘sh qoldirilsa saytda «Ma’lumotnoma» matni ko‘rsatiladi."
                  items={form.biographyTimeline}
                  getId={(_, index) => `bio-${index}`}
                  summary={(row, index) => row.year || `${index + 1}-qator`}
                  create={() => ({ year: '', text: { uz: '', ru: '', en: '' } })}
                  onChange={(biographyTimeline) =>
                    setForm((current) => ({ ...current, biographyTimeline }))
                  }
                  renderItem={(row, update) => (
                    <>
                      <Input
                        label="Yil"
                        value={row.year}
                        placeholder="1981 yoki 1981–1995"
                        onChange={(event) => update({ ...row, year: event.target.value })}
                      />
                      <TranslatableInput
                        label="Matn"
                        multiline
                        rows={2}
                        value={row.text}
                        onChange={(text) => update({ ...row, text })}
                      />
                    </>
                  )}
                />
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader title="Aloqa" />
            <CardBody className="space-y-4">
              <MediaPicker
                label="Foto"
                value={form.photo}
                onChange={(photo) => setForm((current) => ({ ...current, photo }))}
              />
              <Input
                label="Telefon"
                value={form.phone}
                placeholder="+998712345678"
                onChange={(event) =>
                  setForm((current) => ({ ...current, phone: event.target.value }))
                }
                error={fieldErrors.phone}
              />
              <Input
                label="Elektron pochta"
                type="email"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({ ...current, email: event.target.value }))
                }
                error={fieldErrors.email}
              />
              <TranslatableInput
                label="Qabul vaqti"
                value={form.receptionHours}
                onChange={(receptionHours) =>
                  setForm((current) => ({ ...current, receptionHours }))
                }
                hint="Masalan: Dushanba, 15:00–17:00"
              />
              <Input
                label="Manzil (slug)"
                value={form.slug}
                onChange={(event) =>
                  setForm((current) => ({ ...current, slug: event.target.value }))
                }
                error={fieldErrors.slug}
              />
            </CardBody>
          </Card>
        </div>
      )}
    />
  );
}
